/**
 * 🌊 Flow API 클라이언트 - Frontend용 HTTP API 클라이언트
 *
 * 🎯 핵심 철학:
 * - Frontend는 HTTP를 통해서만 Backend와 통신
 * - Backend API 클래스를 직접 import하지 않음
 * - 타입 안전성 유지
 * - 브라우저 환경에서 안전하게 작동
 */

// ============================================
// 🎯 타입 정의 (Backend API와 동일한 타입 구조)
// ============================================

// AtomicFlow API 타입
export interface FlowExecutionRequest {
  flowType: 'qty' | 'asset' | 'revenue' | 'expense' | 'equity' | 'liability' | 'action';
  changeInfo: {
    value: number | unknown;
    targetDomain: string;
    targetObject: string;
    targetField: string;
    operation: 'increase' | 'decrease' | 'set' | 'toggle' | 'append';
  };
  executionContext: {
    executedBy: string;
    streamId?: string;
    reason?: string;
    note?: string;
  };
  accountingInfo?: {
    accountCode?: string;
    fiscalYear?: number;
    fiscalPeriod?: number;
  };
}

export interface FlowExecutionResponse {
  success: boolean;
  executedFlow: unknown;
  flowEvent: unknown;
  executionResult: {
    executionTime: number;
    valueFieldChanges: Array<{
      domain: string;
      objectId: string;
      fieldPath: string;
      beforeValue: unknown;
      afterValue: unknown;
      changeAmount?: number;
    }>;
    balanceImpact: {
      assetChange: number;
      equityChange: number;
      liabilityChange: number;
      isBalanced: boolean;
    };
  };
  sideEffects?: unknown;
}

export interface FlowValidationRequest {
  flowData: Partial<unknown>;
  targetContext?: {
    streamId?: string;
    relatedFlows?: unknown[];
  };
}

export interface FlowValidationResponse {
  isValid: boolean;
  validationResults: {
    structureValid: boolean;
    balanceValid: boolean;
    permissionValid: boolean;
    businessLogicValid: boolean;
  };
  errors: string[];
  warnings: string[];
  suggestions: string[];
  balanceCheck?: unknown;
}

// Stream API 타입
export interface StreamQueryParams {
  page?: number;
  pageSize?: number;
  streamType?: string;
  businessCategory?: string;
  executionStatus?: string;
  search?: string;
  currency?: string;
}

export interface StreamListResponse {
  data: unknown[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean; // ✅ 표준화: hasPreviousPage → hasPrevPage
}

export interface StreamCreateRequest {
  name: string;
  description?: string;
  streamType: string;
  businessCategory: string;
  flowTemplates: unknown[];
}

export interface StreamExecutionRequest {
  streamId: string;
  executionContext: {
    executedBy: string;
    reason?: string;
    note?: string;
  };
}

export interface StreamExecutionResponse {
  success: boolean;
  streamId: string;
  executionResult: unknown;
  flowExecutions: unknown[];
  balanceValidation: unknown;
}

// Template API 타입
export interface TemplateQueryParams {
  page?: number;
  pageSize?: number;
  templateCategory?: string;
  templateType?: string;
  minUsageCount?: number;
  minSuccessRate?: number;
  search?: string;
}

export interface TemplateListResponse {
  data: unknown[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean; // ✅ 표준화: hasPreviousPage → hasPrevPage
}

export interface TemplateCreateRequest {
  name: string;
  description?: string;
  templateCategory?: string;
  flowTemplate: unknown[];
}

export interface TemplateExecutionRequest {
  templateId: string;
  variables: Record<string, unknown>;
  executionContext: {
    executedBy: string;
    reason?: string;
    note?: string;
  };
}

// Flow Assignee API 타입 (FlowAssigneeTypes SSOT와 동일 구조)
export interface FlowAssignmentRequest {
  flowId: string;
  assignedToEntityId: string;
  assignedByEntityId: string;
  assigneeRole: 'owner' | 'executor' | 'reviewer' | 'observer';
  permissions: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  deadline?: Date;
  estimatedDuration?: number;
  reason?: string;
  tags?: string[];
}

export interface BusinessDomainFlowExecuteRequest {
  executionInput: {
    selectedObjects: string[];
    values: unknown[];
  };
  executedBy: string;
  streamId?: string;
  executionOrder?: number;
}

// ============================================
// 🎯 API 응답 타입
// ============================================

// ✅ 표준화: 백엔드 표준 ApiResponse 사용
import type { ApiResponse } from '@shared/types/api-common';
import type {
  BoardAssignmentBulkWriteRequest,
  BoardAssignmentBulkWriteResponse,
} from '@shared/flow';
import { logger } from '../../../../shared/logger';

// ============================================
// 🎯 HTTP 클라이언트 유틸리티
// ============================================

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  tenantId: string
): Promise<T> {
  const { config } = await import('../config/environment');
  const { getAuthHeaders } = await import('../utils/jwtUtils');
  const { fetchWithAuth } = await import('./authFetch');

  const url = `${config.api.baseUrl}${endpoint}`;

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-tenant-id': tenantId,
      ...getAuthHeaders(),
      ...options.headers,
    },
    signal: AbortSignal.timeout(config.api.timeout),
  };

  try {
    const { signal: optionsSignal, ...restOptions } = options;
    const { signal: defaultSignal, ...restDefaults } = defaultOptions;
    const signal = optionsSignal ?? defaultSignal;

    const response = await fetchWithAuth(url, {
      ...restDefaults,
      ...restOptions,
      ...(signal !== undefined ? { signal } : {}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    return await response.json();
  } catch (error) {
    logger.error(`❌ API Request Error: ${endpoint}`, error instanceof Error ? error : undefined, {
      raw: error,
    });
    throw error;
  }
}

// ============================================
// 🎯 Flow API 클라이언트
// ============================================

/**
 * Flow API 클라이언트 - HTTP 기반 Frontend 클라이언트
 */
export class FlowApiClient {
  private tenantId: string;

  constructor(tenantId: string) {
    this.tenantId = tenantId;
  }

  // ============================================
  // ⚛️ Atomic Flow API
  // ============================================

  /**
   * BusinessDomainFlow 실행 (POST /api/flow/business-domain-flow/:flowId/execute)
   */
  async executeBusinessDomainFlow(
    flowId: string,
    request: BusinessDomainFlowExecuteRequest
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/business-domain-flow/${encodeURIComponent(flowId)}/execute`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('BusinessDomainFlow 실행 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * BusinessDomainFlow 상세 조회
   */
  async getBusinessDomainFlow(flowId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/business-domain-flow/${encodeURIComponent(flowId)}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('BusinessDomainFlow 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Atomic Flow 실행
   */
  async executeFlow(request: FlowExecutionRequest): Promise<FlowExecutionResponse> {
    try {
      return await apiRequest<FlowExecutionResponse>(
        '/api/flow/atomic/execute',
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 실행 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * Flow 검증
   */
  async validateFlow(request: FlowValidationRequest): Promise<FlowValidationResponse> {
    try {
      return await apiRequest<FlowValidationResponse>(
        '/api/flow/atomic/validate',
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 검증 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * Flow 롤백
   */
  async rollbackFlow(flowId: string, rollbackRequest?: unknown): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/atomic/${flowId}/rollback`,
        {
          method: 'POST',
          body: JSON.stringify(rollbackRequest || {}),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 롤백 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  // ============================================
  // 🌊 Stream API
  // ============================================

  /**
   * Stream 목록 조회
   */
  async getStreamList(params: StreamQueryParams = {}): Promise<StreamListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.streamType) queryParams.append('streamType', params.streamType);
      if (params.businessCategory) queryParams.append('businessCategory', params.businessCategory);
      if (params.executionStatus) queryParams.append('executionStatus', params.executionStatus);
      if (params.search) queryParams.append('search', params.search);
      if (params.currency) queryParams.append('currency', params.currency);

      const queryString = queryParams.toString();
      const endpoint = `/api/flow/stream${queryString ? `?${queryString}` : ''}`;

      return await apiRequest<StreamListResponse>(endpoint, { method: 'GET' }, this.tenantId);
    } catch (error) {
      logger.error('Stream 목록 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Stream 생성
   */
  async createStream(request: StreamCreateRequest): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        '/api/flow/stream',
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 생성 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * Stream 실행
   */
  async executeStream(request: StreamExecutionRequest): Promise<StreamExecutionResponse> {
    try {
      return await apiRequest<StreamExecutionResponse>(
        `/api/flow/stream/${request.streamId}/execute`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 실행 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * Stream 균형 검증
   */
  async validateStreamBalance(streamId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/stream/${streamId}/validate-balance`,
        { method: 'POST' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 균형 검증 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Stream 성과 조회
   */
  async getStreamPerformance(streamId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/stream/${streamId}/performance`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 성과 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  // ============================================
  // 🌊 BusinessDomainStream API (Template API 대체)
  // ============================================

  /**
   * BusinessDomainStream 상세 조회
   */
  async getBusinessDomainStream(streamId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/v2/stream-templates/${streamId}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 상세 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * BusinessDomainStream 목록 조회 (Template 목록 조회 대체)
   */
  async getBusinessDomainStreamList(
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
    } = {}
  ): Promise<{
    data: unknown[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean; // ✅ 표준화: hasPreviousPage → hasPrevPage
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params.search) queryParams.append('search', params.search);

      const queryString = queryParams.toString();
      const endpoint = `/api/flow/v2/stream-templates${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest<{
        success: boolean;
        data: {
          data: unknown[];
          totalItems: number;
          totalPages: number;
          currentPage: number;
          pageSize: number;
          hasNextPage: boolean;
          hasPrevPage: boolean; // ✅ 표준화: hasPreviousPage → hasPrevPage
        };
      }>(endpoint, { method: 'GET' }, this.tenantId);

      return response.data;
    } catch (error) {
      logger.error('Stream 목록 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * BusinessDomainStream 생성 (Template 생성 대체)
   */
  async createBusinessDomainStream(request: {
    name: string;
    description?: string;
    selectedFlows: Array<{
      flowId: string;
      executionOrder: number;
      isRequired: boolean;
      condition?: string;
    }>;
    compositionRules: {
      balanceRules: {
        requiredEquation: string;
        autoCheck: boolean;
        tolerance: number;
      };
      executionPolicy?: {
        failurePolicy: 'stop' | 'continue' | 'rollback';
        allowParallel?: boolean;
      };
    };
    contextSelection: Array<{
      label: string;
      targetBusinessDomain: string;
      multiplicity: 'single' | 'multiple';
      isRequired: boolean;
    }>;
  }): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        '/api/flow/v2/stream-templates',
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 생성 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * BusinessDomainStream 실행 (Template 실행 대체)
   */
  async executeBusinessDomainStream(
    streamId: string,
    request: {
      streamContext: Record<string, string | string[]>;
      executedBy: string;
    }
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/v2/stream-templates/${streamId}/execute`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Stream 실행 실패:', error instanceof Error ? error : undefined, { raw: error });
      throw error;
    }
  }

  /**
   * Template 검증 (레거시)
   */
  async validateTemplate(
    templateId: string,
    variables?: Record<string, unknown>
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/template/${templateId}/validate`,
        {
          method: 'POST',
          body: JSON.stringify({ variables: variables || {} }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Template 검증 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Template 복제
   */
  async cloneTemplate(templateId: string, newName?: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/flow/template/${templateId}/clone`,
        {
          method: 'POST',
          body: JSON.stringify({ newName }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Template 복제 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  // ============================================
  // 👥 Flow Assignee API
  // ============================================

  /**
   * Flow 담당자 배정
   */
  async assignFlowToEntity(userId: string, request: FlowAssignmentRequest): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 담당자 배정 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Task assignment(Flow 기준) 목록 조회 — primary path
   */
  async getTaskAssignmentsByFlow(userId: string, flowId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/task-assignments/flow/${flowId}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error(
        'Task assignment(Flow 기준) 목록 조회 실패:',
        error instanceof Error ? error : undefined,
        {
          raw: error,
        }
      );
      throw error;
    }
  }

  /**
   * Flow 담당자 목록 조회 (legacy alias wrapper)
   */
  async getFlowAssignees(userId: string, flowId: string): Promise<ApiResponse> {
    return this.getTaskAssignmentsByFlow(userId, flowId);
  }

  /**
   * Entity가 담당자인 Flow 목록 조회
   */
  async getFlowsByAssignee(
    userId: string,
    entityId: string,
    params?: { status?: string; tags?: string }
  ): Promise<ApiResponse> {
    try {
      const query = new URLSearchParams();
      if (params?.status) {
        query.set('status', params.status);
      }
      if (params?.tags) {
        query.set('tags', params.tags);
      }
      const suffix = query.size > 0 ? `?${query.toString()}` : '';
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/assignee/${entityId}${suffix}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('담당자별 Flow 목록 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 실행 권한 검증
   */
  async validateFlowExecutionPermission(
    userId: string,
    flowId: string,
    entityId: string,
    action: 'read' | 'execute' | 'update' | 'approve' | 'reject' | 'complete' = 'execute'
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/validate/${flowId}/${entityId}`,
        {
          method: 'POST',
          body: JSON.stringify({ action }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 실행 권한 검증 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 담당자 성과 통계 조회
   */
  async getAssigneeStats(userId: string, entityId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/stats/${entityId}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 담당자 성과 통계 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 담당자 배정 해제
   */
  async unassignFlowFromEntity(userId: string, assignmentId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/${assignmentId}`,
        { method: 'DELETE' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 담당자 배정 해제 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 담당자 권한 수정
   */
  async updateAssigneePermissions(
    userId: string,
    assignmentId: string,
    permissions: string[]
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/${assignmentId}/permissions`,
        {
          method: 'PUT',
          body: JSON.stringify({ permissions }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 담당자 권한 수정 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 담당자 위임
   */
  async delegateFlowAssignment(
    userId: string,
    assignmentId: string,
    delegateToEntityId: string
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/${assignmentId}/delegate`,
        {
          method: 'POST',
          body: JSON.stringify({ delegateToEntityId }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 담당자 위임 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * TA6 — tenant-wide board projection (task-centric + unassigned tasks)
   */
  async getAssignmentBoardProjection(userId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/board/projection`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('보드 배정 투영 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * W6 D-2 v2 — Operation Assignment Board heuristic recommendation
   */
  async getBoardAssigneeRecommendations(
    userId: string,
    params: {
      actorEntityId: string;
      excludeEntityId?: string | string[];
      requiredRole?: string;
      skillTags?: string[];
      deadline?: string;
    }
  ): Promise<ApiResponse> {
    try {
      const query = new URLSearchParams();
      query.set('actorEntityId', params.actorEntityId);
      if (params.excludeEntityId) {
        const excludes = Array.isArray(params.excludeEntityId)
          ? params.excludeEntityId
          : [params.excludeEntityId];
        for (const entityId of excludes) {
          query.append('excludeEntityId', entityId);
        }
      }
      if (params.requiredRole) {
        query.set('requiredRole', params.requiredRole);
      }
      if (params.skillTags?.length) {
        query.set('skillTags', params.skillTags.join(','));
      }
      if (params.deadline) {
        query.set('deadline', params.deadline);
      }
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/board/recommendations?${query.toString()}`,
        { method: 'GET' },
        this.tenantId
      );
    } catch (error) {
      logger.error('보드 담당자 추천 조회 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * W6 — Operation Assignment Board bulk write
   */
  async bulkBoardAssignmentWrite(
    userId: string,
    request: BoardAssignmentBulkWriteRequest
  ): Promise<ApiResponse<BoardAssignmentBulkWriteResponse>> {
    try {
      return await apiRequest<ApiResponse<BoardAssignmentBulkWriteResponse>>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/board/bulk`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('보드 배정 일괄 처리 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 완료 처리
   */
  async completeFlow(userId: string, flowId: string, entityId: string): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/complete/${flowId}/${entityId}`,
        { method: 'POST' },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 완료 처리 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  /**
   * Flow 승인 결정 기록 (approve / reject)
   */
  async recordFlowApprovalDecision(
    userId: string,
    flowId: string,
    entityId: string,
    decision: 'approve' | 'reject' | 'hold' | 'request_revision',
    reason?: string
  ): Promise<ApiResponse> {
    try {
      return await apiRequest<ApiResponse>(
        `/api/tenant/${this.tenantId}/user/${userId}/flow-assignments/approval/${flowId}/${entityId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            decision,
            ...(reason?.trim() ? { reason: reason.trim() } : {}),
          }),
        },
        this.tenantId
      );
    } catch (error) {
      logger.error('Flow 승인 결정 기록 실패:', error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  // ============================================
  // 🎯 범용 CRUD 시그니처 (domain-hooks useDomainMutations · backendApiClient와 정렬)
  // ============================================

  /**
   * 범용 CRUD 호환: create
   */
  async create(data: unknown): Promise<ApiResponse> {
    return this.createStream(data as StreamCreateRequest);
  }

  /**
   * 범용 CRUD 호환: update
   */
  async update(_id: string, _data: unknown): Promise<ApiResponse> {
    // Flow 관련 도메인은 업데이트 개념이 다를 수 있음
    // 기본적으로는 에러 반환
    return {
      success: false,
      error: 'Flow 관련 도메인은 update를 지원하지 않습니다',
      message: 'Flow는 불변 객체입니다. 새 Flow를 생성하세요.',
    };
  }

  /**
   * 범용 CRUD 호환: delete
   */
  async delete(_id: string): Promise<ApiResponse> {
    // Flow 관련 도메인은 삭제 개념이 다를 수 있음
    // 기본적으로는 에러 반환
    return {
      success: false,
      error: 'Flow 관련 도메인은 delete를 지원하지 않습니다',
      message: 'Flow는 삭제할 수 없습니다. Flow는 불변 기록입니다.',
    };
  }
}

// ============================================
// 🎯 팩토리 함수
// ============================================

/**
 * Flow API 클라이언트 생성
 */
export function createFlowApiClient(tenantId: string): FlowApiClient {
  return new FlowApiClient(tenantId);
}

export default FlowApiClient;
